import defaultLocale from '@/stores/defaultLocale'
import getLocale from '@/stores/locales'
import formatOperationalModel from '@/useCases/formatOperationalModel/formatOperationalModel'
import { useLazyQuery } from '@apollo/client'
import { useCartContext } from '@mercadoni/sinister/dist/cjs/cart'
import {
  getOnlinePaymentProvider,
  UseOPMContext,
} from '@mercadoni/sinister/dist/cjs/checkout'
import { providersConfig } from '@mercadoni/sinister/dist/cjs/providersConfig'
import { ClientModel } from '@mercadoni/sinister/dist/lib/src/entities/clientModel'
import { useEffect } from 'react'
import { OPMData } from '~types/index'
import { useAuthContext } from '../AuthContext'
import getTurnOnOff from './useCases/getTurnOnOff'
import onOPMTurnOff from './useCases/onOPMTurnOff'
import onOPMTurnOn from './useCases/onOPMTurnOn'
import { shouldEnableOPM } from './useCases/shouldEnableOPM'
import shouldRetrieveProvider from './useCases/shouldRetrieveProvider'

interface MiddleWareProps {
  children: React.ReactNode
  clientModel: ClientModel
}

const MiddleWare = ({ children, clientModel }: MiddleWareProps) => {
  const { OPM, setOPM } = UseOPMContext()
  const { customer } = useAuthContext()
  const { cart } = useCartContext()
  const { store, operationalModel } = cart
  const { onePageCheckout: copy = {} } = getLocale() || defaultLocale

  const onSuccessAction = async ({
    getOnlinePaymentProviderForm: resp,
  }: OPMData) => {
    const { providerName } = resp
    const loadedFile = await providersConfig(providerName)
    const { default: getProviderConfig = () => ({}) } = loadedFile || {}
    const config = getProviderConfig({
      copy,
      customer,
      store,
      client: clientModel,
      ...resp,
    })

    setOPM({ ...config, init: true })
  }

  const [getOPM, { data }] = useLazyQuery(getOnlinePaymentProvider, {
    onCompleted: onSuccessAction,
  })

  useEffect(() => {
    const retrieveProvider = shouldRetrieveProvider({
      operationalModel,
      store,
      customer,
    })
    if (retrieveProvider) {
      const operationalModels =
        store.dynamicParams?.onlinePayment?.operationalModel || []

      const enabledOPM = shouldEnableOPM({
        operationalModels,
        opsModel: formatOperationalModel(operationalModel),
        clientConfig: clientModel,
      })

      const { turnOn, turnOff } = getTurnOnOff({
        enabledOPM,
        OPM,
      })

      onOPMTurnOn({
        turnOn,
        data,
        onSuccessAction,
        getOPM,
      })

      onOPMTurnOff({
        turnOff,
        setOPM,
        OPM,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, customer?.id, operationalModel])

  useEffect(() => {
    if (OPM.valid) {
      OPM.onLoaded()
    }
  }, [OPM])

  return <>{children}</>
}

export default MiddleWare
